import { useEffect, useRef, useState } from 'react';
import { Button, Icon, Text, TextField } from '@shopify/polaris';
import { XIcon } from '@shopify/polaris-icons';
import './index.scss';
import Language from '../../../../../../i18n/Language';
import StarIcon from '../../../../../../assets/StarIcon';
import { reqReviewTipsClick, reqOnPageReviewPopupShown } from '../../../../../../api';

const t = Language;

// On-Page 内容编辑评价弹窗（两步）
// 第一步：选星 -> 暂存评分并自动进入第二步
// 第二步：文字反馈 + Send feedback
// 关闭/提交规则见 docs/superpowers/specs/2026-10-15-onpage-review-popup-design.md
const ReviewPopup = (props) => {
    const { open, onClose, toggleToast } = props;

    const [step, setStep] = useState(1);
    const [score, setScore] = useState(0);
    const [hoverScore, setHoverScore] = useState(0); // 悬浮/触摸高亮的星级
    const [feedback, setFeedback] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);

    // 打开时：重置状态
    useEffect(() => {
        if (open) {
            setStep(1);
            setScore(0);
            setHoverScore(0);
            setFeedback('');
            submittingRef.current = false;
            setSubmitting(false);
        }
    }, [open]);

    const handleClose = () => {
        // 回执：在关闭/提交时标记问卷卡片已展示（幂等，失败静默）
        reqOnPageReviewPopupShown({}).catch(() => {});
        if (typeof onClose === 'function') onClose();
    };

    // 提交评分（withDescription=true 时带文字反馈）
    const submit = async (withDescription) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        const params = {
            flag: 7,
            score,
            title: `评价: ${score}星`,
        };
        if (withDescription) {
            params.description = feedback;
        }
        try {
            const res = await reqReviewTipsClick(params);
            if (res && res.ack === 1 && withDescription && typeof toggleToast === 'function') {
                toggleToast(t('Submit successfully'));
            }
        } catch (e) {
            // 失败不阻断关闭
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
            handleClose();
        }
    };

    // 点星：暂存评分并进入第二步
    const handleSelectStar = (n) => {
        if (submitting) return;
        setScore(n);
        setStep(2);
    };

    // 右上角 ×
    const handleCloseIcon = () => {
        if (step === 1) {
            handleClose(); // 未选星，不提交
        } else {
            submit(false); // 仅提交星级
        }
    };

    if (!open) return null;

    return (
        <div className="onpage-review-floating">
            <div className="onpage-review-popup-card">
                <div className="onpage-review-popup-close">
                    <Button variant="tertiary" icon={XIcon} onClick={handleCloseIcon} disabled={submitting} />
                </div>

                {step === 1 ? (
                    <div className="onpage-review-popup-step1">
                        <Text variant="headingMd" as="h2" alignment="center">
                            {t('How would you rate your experience with the Content Editor?')}
                        </Text>
                        <div className="onpage-review-stars" onMouseLeave={() => setHoverScore(0)}>
                            {[1, 2, 3, 4, 5].map((num) => (
                                <div
                                    key={num}
                                    className={`onpage-review-star ${(hoverScore || score) >= num ? 'is-active' : ''}`}
                                    onMouseEnter={() => setHoverScore(num)}
                                    onClick={() => handleSelectStar(num)}
                                    onTouchStart={(e) => {
                                        e && e.preventDefault(); // 防止移动端双击缩放
                                        setHoverScore(num);
                                        handleSelectStar(num);
                                    }}
                                >
                                    <Icon source={StarIcon} />
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="onpage-review-popup-step2">
                        <Text variant="headingMd" as="h2" alignment="center">
                            {t("Anything else you'd like to share?")}
                        </Text>
                        <div className="onpage-review-popup-textarea">
                            <TextField
                                placeholder={t('Tell us what you liked or what we could improve...')}
                                multiline={4}
                                value={feedback}
                                onChange={(value) => setFeedback(value)}
                                autoComplete="off"
                                disabled={submitting}
                            />
                        </div>
                        <div className="onpage-review-popup-actions">
                            <Button
                                variant="primary"
                                disabled={!feedback.trim() || submitting}
                                loading={submitting}
                                onClick={() => submit(true)}
                            >
                                {t('Send feedback')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewPopup;
